// The SESSION source-graph store: every edge/evidence/passage the adapter
// derives from live Sefaria data during this session is recorded here
// (idempotently - ids are deterministic, so re-renders upsert in place).
// This is what makes the integrity report auditable against what the app
// ACTUALLY showed, not just synthetic fixtures. Memory-only, never persisted,
// and never read back into the rendering path (the DOM projection stays the
// source of truth for the UI).

import { makeStore, type SourceGraphStore } from "./sourceGraphQuery.ts";
import { passageFromRef, workFromRef, type AdaptedConnection } from "./sourceGraphAdapter.ts";

const store: SourceGraphStore = makeStore();

export function getSessionStore(): SourceGraphStore {
  return store;
}

// Upsert one adapted connection: the edge, its evidence, and (best-effort)
// the passages + works for both endpoints. Passages recorded here carry no
// text (status valid_ref_missing_text) - the audit cares about refs/edges,
// not text caching.
export function recordAdapted(adapted: AdaptedConnection): void {
  const { edge, evidence } = adapted;
  store.edges.set(edge.id, edge);
  for (const ev of evidence) store.evidence.set(ev.id, ev);
  for (const ref of [edge.fromRef, edge.toRef]) {
    const pid = `p:${ref}`;
    if (!store.passages.has(pid)) {
      const passage = passageFromRef(ref);
      if (passage) {
        store.passages.set(passage.id, passage);
        if (!store.works.has(passage.workId)) {
          const work = workFromRef(ref);
          if (work) store.works.set(work.id, work);
        }
      }
    }
  }
}

// Test-only: reset between test cases.
export function _resetSessionStore(): void {
  store.works.clear();
  store.versions.clear();
  store.passages.clear();
  store.edges.clear();
  store.evidence.clear();
  store.topics.clear();
}
